import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Flame } from 'lucide-react';
import { logAnalyticsEvent, ANALYTICS_EVENTS } from '@/utils/Analytics';

interface FreeCapReachedProps {
  /**
   * Daily cap the user hit. Always a real, backend-reported value: callers get
   * it from a parsed DailyLimitError, and parseDailyLimitError refuses to
   * classify errors without a valid finite limit — so no fallback is needed
   * (or allowed) here.
   */
  limit: number;
  /** ISO timestamp when the allowance resets (optional, for messaging). */
  resetsAtUtc?: string;
  /** Quiz test type, for analytics segmentation. */
  testType?: string;
  /** Where the cap was hit, for analytics. */
  context?: 'submit_answer' | 'initialize_session';
}

/**
 * In-flow paywall shown when a metered free-tier user exhausts their daily
 * question allowance. Rendered INSIDE the quiz flow (not a redirect) so the
 * conversion moment lands at peak appetite. Fires the `free_cap_hit` GA4 event
 * on mount — the free tier's core conversion-intent signal.
 *
 * Visual language mirrors the pricing/trial cards: hard `shadow-ledge`,
 * `border-2 border-neutral-200`, `font-display` heading, `btn-primary` CTA,
 * brand `primary-*`/`neutral-*` tokens only — no gradients, no soft shadows.
 */
export const FreeCapReached: React.FC<FreeCapReachedProps> = ({
  limit,
  resetsAtUtc,
  testType,
  context = 'submit_answer',
}) => {
  const navigate = useNavigate();

  React.useEffect(() => {
    logAnalyticsEvent(ANALYTICS_EVENTS.FREE_CAP_HIT, {
      limit,
      test_type: testType,
      context,
      resets_at_utc: resetsAtUtc,
      timestamp: new Date().toISOString(),
    });
    // Fire once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex items-center justify-center min-h-screen bg-neutral-50 px-4">
      <div className="w-full max-w-md rounded-2xl border-2 border-neutral-200 bg-white p-6 sm:p-8 text-center shadow-ledge">
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-primary-100">
          <Lock className="h-7 w-7 text-primary-600" aria-hidden="true" />
        </div>

        <h1 className="font-display text-2xl font-bold text-neutral-900 mb-2">
          That&apos;s your {limit} free questions for today
        </h1>

        <p className="text-neutral-600 mb-6">
          Your streak is safe. Come back tomorrow, or go unlimited.
        </p>

        <div className="mb-6 flex items-center justify-center gap-2 rounded-xl border border-primary-200 bg-primary-50 px-4 py-3">
          <Flame className="h-5 w-5 flex-shrink-0 text-primary-600" aria-hidden="true" />
          <span className="text-sm font-medium text-primary-700">
            Full Access unlocks every question until you pass.
          </span>
        </div>

        <div className="space-y-3">
          <button
            onClick={() => navigate('/pricing')}
            className="btn-primary w-full rounded-xl px-6 py-3 font-display font-semibold"
          >
            Get unlimited access
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full rounded-xl bg-neutral-100 px-6 py-3 font-medium text-neutral-700 transition-colors hover:bg-neutral-200"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    </div>
  );
};
